import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { AppDefinition } from "@acruxsoft/platform-shared";
import type { AppAccess } from "../lib/app-access";

/**
 * Brand marks live in the frontend, not in the shared catalog — the catalog
 * describes routing and entitlements, not artwork. Apps without an asset yet
 * fall back to a lettermark built from their short name.
 *
 * Every product of the suite uses the same swordfish; what tells them apart is
 * the colour of the badge behind it, set by `acx__brand-mark--<appId>` and
 * matched to that app's own brand (teal for TVM, blue for TVD).
 */
const BRAND_MARK_SRC: Record<string, string> = {
  tvm: "/brand-mark.png",
  tvd: "/brand-mark.png"
};

/**
 * Button variant per app, so the CTA carries the colour of the product it
 * opens. Falls back to the platform primary for an app that hasn't been given
 * a palette yet — an unstyled CTA would read as broken.
 */
const BUTTON_VARIANT: Record<string, string> = {
  tvm: "acx__btn--tvm",
  tvd: "acx__btn--tvd"
};

export interface AppTileProps {
  app: AppDefinition;
  access: AppAccess;
  /** End date of the user's license, when the app is open through one. */
  licenseEndsAt: number | null;
  /** When the user's pending request was last sent, for `requested`. */
  requestedAt: number | null;
  /** When the user's license ran out, for `expired`. */
  expiredAt: number | null;
  launching: boolean;
  error: string | null;
  onOpen: (app: AppDefinition) => void;
  onRequest: (app: AppDefinition) => void;
}

export function AppTile(props: AppTileProps) {
  const { app, access, launching, error, onOpen, onRequest } = props;
  const { t, i18n } = useTranslation();
  const shortName = t(`home.apps.${app.appId}.shortName`);
  const markSrc = BRAND_MARK_SRC[app.appId];
  const btn = `acx__btn ${BUTTON_VARIANT[app.appId] ?? "acx__btn--primary"}`;
  // Locked behind a license: the product is shown — that's the point, people
  // should see what else the suite has — but veiled, with the request button
  // as the one crisp thing on the card.
  const veiled = access === "locked" || access === "requested" || access === "expired";
  const fmt = (ms: number) =>
    new Date(ms).toLocaleDateString(i18n.language, { year: "numeric", month: "short", day: "numeric" });

  return (
    <article
      className={[
        "acx__app-tile",
        veiled ? "acx__app-tile--veiled" : "",
        access === "unverified" ? "acx__app-tile--locked" : "",
        access === "comingSoon" ? "acx__app-tile--coming" : ""
      ].filter(Boolean).join(" ")}
    >
      <span className="acx__app-tile-pill">
        <AccessPill app={app} access={access} licenseEndsAt={props.licenseEndsAt} fmt={fmt} />
      </span>

      <div className="acx__app-tile-row">
        <span
          className={`acx__brand-mark acx__brand-mark--${app.appId}${veiled ? " acx__app-tile-veil" : ""}`}
          style={{ width: 52, height: 52 }}
          aria-hidden
        >
          {markSrc
            ? <img src={markSrc} alt="" className="acx__brand-mark-img" />
            : <span className="acx__brand-mark-letters">{shortName}</span>}
        </span>
        <div className="acx__app-tile-head">
          <span className="acx__card-eyebrow">{t(`home.apps.${app.appId}.eyebrow`)}</span>
          <h3 className="acx__app-tile-title">{t(`home.apps.${app.appId}.name`)}</h3>
        </div>
      </div>

      <div className="acx__app-tile-body">
        <p className={`acx__app-tile-desc${veiled ? " acx__app-tile-veil" : ""}`}>
          {t(`home.apps.${app.appId}.description`)}
        </p>
        {veiled && (
          <span className="acx__app-tile-lock" aria-hidden>
            <LockIcon />
          </span>
        )}
      </div>

      <div className="acx__app-tile-cta">
        {access === "comingSoon" && (
          <span className="acx__app-tile-note">{t("home.appTile.comingSoonBody")}</span>
        )}
        {access === "unverified" && (
          <Link to="/verify-email" className={btn}>
            {t("home.appTile.verifyEmail")}
          </Link>
        )}
        {access === "open" && (
          <button onClick={() => onOpen(app)} disabled={launching} className={btn}>
            {launching ? t("home.appTile.opening") : t("home.appTile.open", { app: shortName })}
          </button>
        )}
        {access === "locked" && (
          <>
            <span className="acx__app-tile-note">{t("home.appTile.licenseNeeded")}</span>
            <button onClick={() => onRequest(app)} className={btn}>
              {t("home.appTile.requestLicense")}
            </button>
          </>
        )}
        {access === "expired" && (
          <>
            <span className="acx__app-tile-note">
              {props.expiredAt
                ? t("home.appTile.expiredOn", { date: fmt(props.expiredAt) })
                : t("home.appTile.expired")}
            </span>
            <button onClick={() => onRequest(app)} className={btn}>
              {t("home.appTile.requestRenewal")}
            </button>
          </>
        )}
        {access === "requested" && (
          <>
            <span className="acx__app-tile-note acx__app-tile-note--ok">
              {props.requestedAt
                ? t("home.appTile.requestedOn", { date: fmt(props.requestedAt) })
                : t("home.appTile.requested")}
            </span>
            <button onClick={() => onRequest(app)} className="acx__btn acx__btn--ghost">
              {t("home.appTile.requestAgain")}
            </button>
          </>
        )}
        {error && <span className="acx__alert acx__alert--danger" role="alert">{error}</span>}
      </div>
    </article>
  );
}

function AccessPill({
  app,
  access,
  licenseEndsAt,
  fmt
}: {
  app: AppDefinition;
  access: AppAccess;
  licenseEndsAt: number | null;
  fmt: (ms: number) => string;
}) {
  const { t } = useTranslation();
  switch (access) {
    case "comingSoon":
      return <span className="acx__pill acx__pill--neutral">{t("home.appTile.comingSoonPill")}</span>;
    case "unverified":
      return <span className="acx__pill acx__pill--neutral">{t("home.appTile.unverifiedPill")}</span>;
    case "open":
      if (app.access === "free") {
        return <span className="acx__pill acx__pill--active">{t("home.appTile.freePill")}</span>;
      }
      return (
        <span className="acx__pill acx__pill--active">
          {licenseEndsAt
            ? t("home.appTile.licensedUntilPill", { date: fmt(licenseEndsAt) })
            : t("home.appTile.licensedPill")}
        </span>
      );
    case "requested":
      return <span className="acx__pill acx__pill--pending">{t("home.appTile.requestedPill")}</span>;
    case "expired":
      return <span className="acx__pill acx__pill--neutral">{t("home.appTile.expiredPill")}</span>;
    case "locked":
      return <span className="acx__pill acx__pill--neutral">{t("home.appTile.lockedPill")}</span>;
  }
}

const LockIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11 V 7 a 4 4 0 0 1 8 0 V 11" />
  </svg>
);
